export const faNumber = (value?: number | null, fractionDigits = 0) =>
  (value ?? 0).toLocaleString("fa-IR", { maximumFractionDigits: fractionDigits });

export const faDigits = (value: string) => value.replace(/\d/g, (digit) => "۰۱۲۳۴۵۶۷۸۹"[Number(digit)]);

// Amounts arrive and are shown in rial.
export const rialText = (rial?: number | null) => `${faNumber(Math.round(rial ?? 0))} ریال`;

export function compactRial(rial?: number | null) {
  const value = Math.round(rial ?? 0);
  const size = Math.abs(value);
  if (size >= 1_000_000_000) return { value: faNumber(value / 1_000_000_000, 1), unit: "میلیارد" };
  if (size >= 1_000_000) return { value: faNumber(value / 1_000_000, 1), unit: "میلیون" };
  if (size >= 1_000) return { value: faNumber(value / 1_000), unit: "هزار" };
  return { value: faNumber(value), unit: "ریال" };
}

// "۲٫۴ میلیون ریال": compact, but always in rial.
export function compactRialText(rial?: number | null) {
  const { value, unit } = compactRial(rial);
  return unit === "ریال" ? `${value} ریال` : `${value} ${unit} ریال`;
}

// Next to a total already labelled in rial, "۳۸ میلیون" reads better than repeating the currency.
export const compactAmountText = (rial?: number | null) => {
  const { value, unit } = compactRial(rial);
  return `${value} ${unit}`;
};

// Local calendar date as YYYY-MM-DD, independent of moment's global locale.
export const isoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

type Money = { paid?: number | null; pending?: number | null } | null | undefined;

// Paid and pending are split instead of a change percentage: early in the day a partial total
// compared with a full previous day would always look like a drop.
export function moneyNote(metric: Money, emptyText: string) {
  const paid = metric?.paid ?? 0;
  const pending = metric?.pending ?? 0;
  if (!paid && !pending) return emptyText;
  if (!pending) return "همه وصول شده";
  return `${compactAmountText(paid)} وصول‌شده · ${compactAmountText(pending)} در انتظار`;
}

export const todayTitle = (date = new Date()) =>
  new Intl.DateTimeFormat("fa-IR", { weekday: "long", day: "numeric", month: "long" }).format(date);

export const weekdayName = (date: Date) => new Intl.DateTimeFormat("fa-IR", { weekday: "long" }).format(date);

export const clockText = (time?: string | null) => (time ? faDigits(time.slice(0, 5)) : "—");

export function durationText(minutes: number) {
  if (minutes <= 0) return "";
  if (minutes % 60 === 0) return `${faNumber(minutes / 60)} ساعت`;
  return `${faNumber(minutes)} دقیقه`;
}

export const percentOf = (part: number, total: number) => (total > 0 ? Math.min(100, Math.max(0, (part / total) * 100)) : 0);
